import React from 'react';
import { Show } from '@refinedev/antd';
import { useShow } from '@refinedev/core';
import { Link } from 'react-router-dom';
import { Table, Tag, theme } from 'antd';
import { Field } from '../../components/Field';

// NOTE: this local shape mirrors the API's AdminSleepStepsDayDetailDto.
interface SleepLogRow {
  id: string;
  startedAt: string;
  endedAt: string;
  durationMinutes: number;
  source: string;
  notes: string | null;
}

interface StepLogRow {
  id: string;
  recordedAt: string;
  steps: number;
  source: string;
}

interface SleepStepsDayDetail {
  id: string;
  userId: string;
  displayName: string;
  date: string;
  sleepMinutes: number;
  sleepSessions: number;
  steps: number;
  sleepLogs: SleepLogRow[];
  stepLogs: StepLogRow[];
}

const formatMinutes = (minutes: number) =>
  minutes ? `${Math.floor(minutes / 60)}h ${minutes % 60}m` : '—';

const formatTime = (iso: string) => new Date(iso).toISOString().slice(11, 16);

const sourceTag = (source: string) => (
  <Tag style={{ borderRadius: 9999, padding: '2px 12px' }}>{source}</Tag>
);

export const SleepStepsShow: React.FC = () => {
  const { token } = theme.useToken();
  const { queryResult } = useShow<SleepStepsDayDetail>({ resource: 'sleep-steps' });
  const item = queryResult?.data?.data;

  if (!item) {
    return <div style={{ color: token.colorTextSecondary }}>Loading...</div>;
  }

  return (
    <Show resource="sleep-steps">
      <div style={{ padding: '8px 0' }}>
        <Field label="User">
          <Link to={`/users/show/${item.userId}`}>{item.displayName || item.userId}</Link>
        </Field>
        <Field label="Day (UTC)">{item.date}</Field>
        <Field label="Total Sleep">
          {formatMinutes(item.sleepMinutes)} over {item.sleepSessions} stretch(es)
        </Field>
        <Field label="Total Steps">{item.steps.toLocaleString()}</Field>

        <Field label="Sleep Stretches">
          <Table
            dataSource={item.sleepLogs}
            rowKey="id"
            pagination={false}
            size="small"
            locale={{ emptyText: 'No sleep recorded' }}
          >
            <Table.Column
              title="From"
              render={(_: unknown, row: SleepLogRow) => formatTime(row.startedAt)}
            />
            <Table.Column
              title="To"
              render={(_: unknown, row: SleepLogRow) => formatTime(row.endedAt)}
            />
            <Table.Column
              title="Duration"
              render={(_: unknown, row: SleepLogRow) => formatMinutes(row.durationMinutes)}
            />
            <Table.Column
              title="Source"
              render={(_: unknown, row: SleepLogRow) => sourceTag(row.source)}
            />
            <Table.Column
              title="Notes"
              render={(_: unknown, row: SleepLogRow) => row.notes || '—'}
            />
          </Table>
        </Field>

        <Field label="Step Buckets">
          <Table
            dataSource={item.stepLogs}
            rowKey="id"
            pagination={false}
            size="small"
            locale={{ emptyText: 'No steps recorded' }}
          >
            <Table.Column
              title="Bucket start"
              render={(_: unknown, row: StepLogRow) => formatTime(row.recordedAt)}
            />
            <Table.Column
              title="Steps"
              render={(_: unknown, row: StepLogRow) => row.steps.toLocaleString()}
            />
            <Table.Column
              title="Source"
              render={(_: unknown, row: StepLogRow) => sourceTag(row.source)}
            />
          </Table>
        </Field>
      </div>
    </Show>
  );
};
